/**
 * 注文書の2回目 2a（#233、docs/93 §14.3・§14.4、★8）：まとめ発注・{vendorQuote}の採用／不採用・辞退の連絡（任意）・見積書の行から。
 *   ・採用／不採用は印だけ（何度でも変えられる・確かめの窓なし・通知の「元に戻す」）。**不採用は #198 の不採用と1つ**（2026-10-25 射場さん）
 *   ・{order}を出しても、ほかの回答を自動で不採用にしない（★8）
 *   ・まとめて作成は 2件以上選んだときだけ（1件は「{order}作成」）。同じ{vendor}は1枚・依頼ごとに見出しの行。{vendor}が違えば各社1枚
 *   ・辞退の連絡は不採用の{vendor}に任意で送る（自動では送らない・金額は入れない・送付の記録に残す・送ったあとも採用に戻せる）
 * 画面に出す文はここ（ORDER_BUNDLE_MSG）に1か所。{order} {vendor} {vendorQuote} {project} は会社の言葉（画面は t()、API は fillLabels）
 */
import { z } from "zod";

const Uuid = z.string().uuid();

/** 帳票の{order}の参照（しるし・確かめの窓）。no＝番号（下書きは null） */
export const OrderRef = z.object({ id: Uuid, no: z.string().nullable() });
export type OrderRef = z.infer<typeof OrderRef>;

/** 採用／不採用（NULL＝未選択） */
export const VendorQuoteAdoption = z.enum(["adopted", "declined"]);
export type VendorQuoteAdoption = z.infer<typeof VendorQuoteAdoption>;
/** PUT /vendor-quotes/:id/adoption（印だけ。null＝未選択に戻す） */
export const VendorQuoteAdoptionUpdate = z.object({ adoption: VendorQuoteAdoption.nullable() });
export type VendorQuoteAdoptionUpdate = z.infer<typeof VendorQuoteAdoptionUpdate>;

/** 回答の比較の1行（同じ{project}・同じ件名の見積依頼への回答。docs/79 の形：{vendor}・税抜の合計・回答日・採用の印） */
export const VendorQuoteCompareItem = z.object({
  vendorQuoteId: Uuid,
  vendorName: z.string(),
  /** 依頼の番号（RQ-0001） */
  rfqNo: z.string().nullable(),
  totalExTax: z.number().int().nullable(),
  /** 回答日（登録した日。YYYY-MM-DD） */
  answeredOn: z.string(),
  adoption: VendorQuoteAdoption.nullable(),
  /** 反映した行がある（不採用にできない） */
  used: z.boolean(),
  /** いま開いている回答 */
  current: z.boolean(),
});
export type VendorQuoteCompareItem = z.infer<typeof VendorQuoteCompareItem>;

/** 辞退の連絡を送った記録（いちばん新しいもの） */
export const DeclineMailSent = z.object({ deliveryId: Uuid, at: z.string() });
export type DeclineMailSent = z.infer<typeof DeclineMailSent>;

/** GET /vendor-quotes/:id/decline-mail：窓の初めの値（差し込みを埋めた件名・本文と宛先の候補） */
export const DeclineMailDraft = z.object({
  to: z.string().nullable(),
  subject: z.string(),
  body: z.string(),
  /** 控え Bcc に入る送信者のメールアドレス */
  senderEmail: z.string(),
  sent: DeclineMailSent.nullable(),
});
export type DeclineMailDraft = z.infer<typeof DeclineMailDraft>;

const Email = z.string().trim().email("メールアドレスの形を確かめてください").max(254);
/** POST /vendor-quotes/:id/decline-mail */
export const DeclineMailSend = z.object({
  to: z.array(Email).min(1, "宛先を入力してください").max(10),
  subject: z.string().trim().min(1, "件名を入力してください").max(200),
  body: z.string().trim().min(1, "本文を入力してください").max(4000),
  bccSelf: z.boolean().default(true),
});
export type DeclineMailSend = z.infer<typeof DeclineMailSend>;
export type DeclineMailSendInput = z.input<typeof DeclineMailSend>;
export const DeclineMailResult = z.object({ deliveryId: Uuid });
export type DeclineMailResult = z.infer<typeof DeclineMailResult>;

/** まとめて作成の上限（見積依頼・回答を合わせて） */
export const ORDER_BUNDLE_LIMITS = { sources: 50 } as const;

/** POST /orders/bulk：見積依頼（回答があれば回答の行・無ければ依頼書の行）と{vendorQuote}を選んで、{vendor}ごとに1枚ずつ下書き */
export const OrderBulkCreate = z
  .object({
    rfqIds: z.array(Uuid).max(ORDER_BUNDLE_LIMITS.sources).default([]),
    vendorQuoteIds: z.array(Uuid).max(ORDER_BUNDLE_LIMITS.sources).default([]),
  })
  .refine((x) => x.rfqIds.length + x.vendorQuoteIds.length >= 1, { message: "選んでください" })
  .refine((x) => x.rfqIds.length + x.vendorQuoteIds.length <= ORDER_BUNDLE_LIMITS.sources, { message: "まとめて作成できるのは 50件までです" });
export type OrderBulkCreate = z.infer<typeof OrderBulkCreate>;
export type OrderBulkCreateInput = z.input<typeof OrderBulkCreate>;
export const OrderBulkResult = z.object({
  orders: z.array(z.object({ id: Uuid, vendorName: z.string(), lineCount: z.number().int(), sources: z.number().int() })),
});
export type OrderBulkResult = z.infer<typeof OrderBulkResult>;

/** POST /estimates/:id/order-lines（見積の行の「{order}に追加」）：orderId（その{vendor}の下書き）に足す。無ければ新しい{order} */
export const OrderLinesAdd = z.object({ vendorId: Uuid, lineageIds: z.array(Uuid).min(1).max(300), orderId: Uuid.optional() });
export type OrderLinesAdd = z.infer<typeof OrderLinesAdd>;

/** GET /estimates/:id/order-marks：見積の行（系譜）ごとの{order}（取消を除く。下書きも） */
export const EstimateOrderMarks = z.object({ marks: z.array(z.object({ lineageId: Uuid, orders: z.array(OrderRef) })) });
export type EstimateOrderMarks = z.infer<typeof EstimateOrderMarks>;

/** {order}の元（頭の帯の「元」）：見積依頼・{vendorQuote}・見積 */
export const OrderOrigins = z.object({
  rfqs: z.array(z.object({ id: Uuid, no: z.string().nullable() })),
  vendorQuotes: z.array(z.object({ id: Uuid, quoteNo: z.string() })),
  estimates: z.array(z.object({ id: Uuid, no: z.string().nullable() })),
});
export type OrderOrigins = z.infer<typeof OrderOrigins>;

/* ============================================================================
 * 文
 * ========================================================================== */

export const ORDER_BUNDLE_MSG = {
  /** 採用／不採用（{vendorQuote}の一覧・詳細・回答の比較） */
  adopt: "採用",
  decline: "不採用",
  adoptionGroup: "採用・不採用",
  adoptedChip: "採用",
  declinedChip: "不採用",
  adopted: "採用にしました",
  declined: "不採用にしました",
  cleared: "採用・不採用を外しました",
  undo: "元に戻す",
  adoptionFilter: "採用",
  adoptionAll: "すべて",
  adoptionHelp: "採用・不採用は印だけです。何度でも変えられます。{order}を発行しても、ほかの回答は変わりません。見積に反映した{vendorQuote}は不採用にできません（反映した行を解除してから）。",
  /** 回答の比較 */
  compareTitle: "回答の比較",
  compareHelp: "同じ{project}・同じ件名の見積依頼への回答を並べます。採用・不採用は右端でも付けられます。",
  compareVendor: "{vendor}",
  compareAmount: "金額（税抜）",
  compareDate: "回答日",
  compareMark: "採用",
  compareCurrent: "表示中",
  compareOpen: "表示",
  /** 辞退の連絡（任意） */
  declineMail: "辞退連絡",
  declineMailTitle: "辞退の連絡",
  declineMailHelp: "不採用の{vendor}に、今回は見送ることをメールで知らせます。送らなくても構いません。送ったあとも採用に戻せます。文面に金額は入りません。",
  declineTo: "宛先",
  declineSubject: "件名",
  declineBody: "本文",
  declineBccSelf: "控え Bcc",
  declineSend: "送信",
  declineSending: "送信中…",
  declineSent: "辞退の連絡を送信しました",
  declineSentAt: "辞退の連絡 {md} 送信済",
  declineNotDeclined: "辞退の連絡は不採用の{vendorQuote}だけです",
  declineNoVendor: "{vendor}が未設定です",
  declineDoc: "御見積書",
  declineLabel: "辞退の連絡",
  /** まとめて作成（見積依頼の一覧・{vendorQuote}の一覧） */
  bundle: "まとめて作成",
  selected: "{n}件を選択",
  clearSelection: "選択解除",
  selectRow: "選択",
  selectAll: "すべて選択",
  created: "{n}枚の下書きを作成しました",
  createdOne: "{order}の下書きを作成しました",
  /** 2枚目の確かめ（止めない） */
  alreadyTitle: "{order}作成",
  already: "{nos} を作成済みです。もう1枚作りますか",
  alreadyDraft: "{order}の下書き",
  alreadyDraftOnly: "{order}の下書きを作成済みです。もう1枚作りますか",
  alreadyDo: "作成",
  /** しるし（見積依頼の一覧の行・依頼書の頭・見積の行の仕入元の下段） */
  ordered: "発注済 {no}",
  orderedDraft: "{order}下書き",
  orderedMore: "ほか {n}件",
  filterOrdered: "発注済",
  /** {order}の頭の帯の「元」 */
  originRfqs: "見積依頼 {n}件",
  /** 見積書の行の「…」 */
  addToOrder: "{order}に追加",
  addToOrderTitle: "{order}に追加",
  addTarget: "追加先",
  addNew: "新しい{order}",
  addDo: "追加",
  added: "{order}に追加しました",
  open: "表示",
  /** API の断り */
  rfqDraft: "送付していない見積依頼は選べません",
  rfqCancelled: "取消した見積依頼は選べません",
  noVendor: "{vendor}が未設定の{vendorQuote}は選べません",
  vendorQuoteReading: "AI読み取り中の{vendorQuote}は選べません",
  notFound: "選んだものが見つかりません",
  noLines: "{order}にする行がありません",
  targetNotDraft: "発行した{order}には追加できません",
  targetVendor: "追加先の{order}の{vendor}が違います",
} as const;

/** 見出しの行（まとめて作成：依頼ごとに）。「空調設備（RQ-0003）」 */
export function bundleHeading(subject: string, no: string | null): string {
  const s = subject.trim();
  return (no ? (s ? `${s}（${no}）` : no) : s).slice(0, 200);
}

/** 2枚目の確かめの文（{order}は呼ぶ側が埋める）：「PO-0001・PO-0002 を作成済みです。もう1枚作りますか」（下書きだけなら「{order}の下書きを作成済みです。…」） */
export function alreadyOrderedText(refs: ReadonlyArray<OrderRef>): string {
  const nos = [...new Set(refs.map((r) => r.no).filter((x): x is string => !!x))];
  const drafts = refs.some((r) => !r.no);
  if (!nos.length) return ORDER_BUNDLE_MSG.alreadyDraftOnly;
  return ORDER_BUNDLE_MSG.already.replace("{nos}", [...nos, ...(drafts ? [ORDER_BUNDLE_MSG.alreadyDraft] : [])].join("・"));
}

/** しるしの文：発行したものを先に「発注済 PO-0001」、下書きだけなら「{order}下書き」、2件目からは「ほか n件」 */
export function orderedMarkText(refs: ReadonlyArray<OrderRef>): string {
  if (!refs.length) return "";
  const issued = refs.filter((r) => r.no).sort((a, b) => (a.no ?? "").localeCompare(b.no ?? "", "ja", { numeric: true }));
  const first = issued[0];
  const head = first ? ORDER_BUNDLE_MSG.ordered.replace("{no}", first.no!) : ORDER_BUNDLE_MSG.orderedDraft;
  return refs.length > 1 ? `${head} ${ORDER_BUNDLE_MSG.orderedMore.replace("{n}", String(refs.length - 1))}` : head;
}
